import React, { useEffect, useRef, useState } from "react";
import { DatabaseBackup, Download, Trash2, Upload, ShieldAlert } from "lucide-react";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState, Loader } from "@/components/dashboard/ui";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function AdminBackups() {
  const { hasPerm } = useAuth();
  const canRestore = hasPerm("restore_backups");
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/backups").then(({ data }) => setItems(data || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try { await api.post("/backups"); toast.success("Sauvegarde créée"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const download = (b) => {
    const token = localStorage.getItem("delya_token");
    window.open(`${API}/backups/${b.backup_id}/download?auth=${token}`, "_blank");
  };
  const del = async (b) => { if (!window.confirm("Supprimer cette sauvegarde ?")) return; await api.delete(`/backups/${b.backup_id}`); load(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPendingFile(file);
    const fd = new FormData(); fd.append("file", file); fd.append("confirm", "no");
    try { const { data } = await api.post("/backups/import", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPreview(data.manifest); }
    catch (er) { toast.error(apiError(er.response?.data?.detail)); setPendingFile(null); }
    if (fileRef.current) fileRef.current.value = "";
  };
  const confirmRestore = async () => {
    setRestoring(true);
    const fd = new FormData(); fd.append("file", pendingFile); fd.append("confirm", "yes");
    try { await api.post("/backups/import", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Restauration terminée"); setPreview(null); setPendingFile(null); load(); }
    catch (er) { toast.error(apiError(er.response?.data?.detail)); }
    finally { setRestoring(false); }
  };

  if (items === null) return <Loader />;
  return (
    <div>
      <PageHeader title="Sauvegardes" subtitle="Exportez et restaurez l'ensemble de la configuration et des données."
        action={
          <div className="flex gap-2">
            {canRestore && <>
              <input ref={fileRef} type="file" accept=".zip" className="hidden" onChange={onFile} />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-backup-btn"><Upload className="h-4 w-4 mr-1.5" />Importer</Button>
            </>}
            <Button className="bg-primary" onClick={create} disabled={creating} data-testid="create-backup-btn"><DatabaseBackup className="h-4 w-4 mr-1.5" />{creating ? "Création..." : "Créer une sauvegarde"}</Button>
          </div>
        } />
      <div className="mb-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-3">
        <ShieldAlert className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
        Les sauvegardes n'incluent jamais de secrets (clés API, mots de passe, jetons OAuth). Une sauvegarde de sécurité automatique est créée avant chaque restauration.
      </div>
      {items.length === 0 ? <EmptyState icon={DatabaseBackup} title="Aucune sauvegarde" text="Créez votre première sauvegarde complète." /> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Créé par</th><th className="px-4 py-3">Taille</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
            <tbody>{items.map((b) => (
              <tr key={b.backup_id} className="border-t border-slate-100" data-testid={`backup-${b.backup_id}`}>
                <td className="px-4 py-3 text-slate-600">{new Date(b.created_at).toLocaleString("fr-CH")}</td>
                <td className="px-4 py-3">{b.created_by}</td>
                <td className="px-4 py-3 text-slate-500">{(b.size / 1024).toFixed(0)} Ko</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs">{b.status}</span></td>
                <td className="px-4 py-3"><div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => download(b)}><Download className="h-4 w-4" /></Button>
                  <button onClick={() => del(b)}><Trash2 className="h-4 w-4 text-rose-400 hover:text-rose-600" /></button>
                </div></td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}
      <AlertDialog open={!!preview} onOpenChange={(o) => !o && !restoring && (setPreview(null), setPendingFile(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurer cette sauvegarde ?</AlertDialogTitle>
            <AlertDialogDescription>
              {preview && <span className="block space-y-1">
                <span className="block">Date : {preview.created_at && new Date(preview.created_at).toLocaleString("fr-CH")}</span>
                <span className="block">Version : {preview.backup_version} · Schéma {preview.schema_version}</span>
                <span className="block">Contenu : {preview.counts && Object.entries(preview.counts).map(([k, v]) => `${v} ${k}`).join(" · ")}</span>
                <span className="block mt-2 font-medium text-rose-600">Cette opération peut remplacer des données existantes. Une sauvegarde de sécurité sera créée automatiquement avant.</span>
              </span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmRestore(); }} disabled={restoring} data-testid="confirm-restore">{restoring ? "Restauration..." : "Confirmer la restauration"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
